// Campaigns: a goal, who it's for and dated posts, Stories, texts and emails,
// running from a day to six weeks. Stored on the server per location
// (marketing/campaign-group; per stylist for the self-employed), with this
// browser's copy as a cache so the Studio and lists read synchronously and a
// campaign made offline keeps working. QA (2026-09-30): they used to live only
// in the browser that made them, so another device or another member of staff
// couldn't see, cancel or duplicate them.
//
// The emails and texts in an approved campaign are ordinary scheduled
// campaigns on the server (each carries groupId = this id); this is the
// grouping.
//
// {
//   id, name, why, kind, planKey (Bailey's plan it came from, if any),
//   start, end ('YYYY-MM-DD'), audience: { segment, label, count },
//   items: [{ id, channel: 'post'|'story'|'sms'|'email', at, on, title, post|message|email }],
//   status: 'draft' | 'approved' | 'cancelled',
//   created: { [itemId]: { channel, id } },    what approving made
//   copiedFrom: { salonId, salonName } | null,  made by Copy to other locations
//   createdAt, updatedAt, approvedAt
// }
//
// Sync: saves go to the cache at once (marked _dirty) and up to the server,
// the latest per campaign winning. hydrateCampaigns() pulls the server's list
// and keeps anything still waiting here on top; the first time it runs in a
// browser it moves that browser's campaigns up.
import moment from 'moment'
import { marketingBackend } from '../../../services/marketingBackend'
import { itemState } from './itemState'

const EVENT = 'beu-campaigns'
const MAX = 200
const here = () => { try { return localStorage.getItem('salonId') || 'me' } catch (e) { return 'me' } }
const KEY = (salonId) => `beu-campaigns-${salonId || here()}`
const DELETED = () => `beu-campaigns-deleted-${here()}`
const MIGRATED = () => `beu-campaigns-migrated-${here()}`
const online = () => { try { return !!localStorage.getItem('accessToken') } catch (e) { return false } }

const readJson = (k, fallback) => { try { const v = JSON.parse(localStorage.getItem(k) || 'null'); return v == null ? fallback : v } catch (e) { return fallback } }
const writeJson = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)) } catch (e) { /* private mode */ } }

export const listCampaigns = (salonId) => {
  const raw = readJson(KEY(salonId), [])
  return Array.isArray(raw) ? raw : []
}

const write = (list, salonId) => {
  const kept = list.slice(0, MAX)
  try { localStorage.setItem(KEY(salonId), JSON.stringify(kept)) } catch (e) {
    // Full: drop the post thumbnails, then try once more
    try { localStorage.setItem(KEY(salonId), JSON.stringify(kept.map(c => ({ ...c, items: c.items.map(i => (i.post ? { ...i, post: { ...i.post, thumb: null } } : i)) })))) } catch (x) { /* this page still has it */ }
  }
  try { window.dispatchEvent(new CustomEvent(EVENT)) } catch (e) { /* old browsers */ }
  return kept
}

// ── the server side ──────────────────────────────────────────────────────
const writes = {}            // id -> { latest, running }
let refusedOnce = false
const forServer = ({ _dirty, ...c }) => c

const settle = (id, sent) => {
  const list = listCampaigns()
  const i = list.findIndex(c => c.id === id)
  if (i < 0 || list[i].updatedAt !== sent.updatedAt) return // changed again since
  const { _dirty, ...clean } = list[i]
  list[i] = clean
  write(list)
}

const runWrite = async (id) => {
  const w = writes[id]
  if (!w || w.running) return
  w.running = true
  while (w.latest) {
    const job = w.latest
    w.latest = null
    try {
      if (job.kind === 'save') {
        await marketingBackend.campaignGroupSave(id, forServer(job.campaign))
        settle(id, job.campaign)
      } else {
        await marketingBackend.campaignGroupRemove(id)
        const d = readJson(DELETED(), {}); delete d[id]; writeJson(DELETED(), d)
      }
    } catch (e) {
      // Kept (as _dirty, or in the deleted list) and sent on the next hydrate.
      if (!refusedOnce && /only|owner|permission|runs your marketing/i.test(String(e && e.message))) {
        refusedOnce = true
        console.warn('Campaigns kept in this browser only:', e.message)
      }
    }
  }
  w.running = false
  if (w.done) { const done = w.done; w.done = null; done() }
}

const queue = (id, job) => {
  if (!online()) return
  writes[id] = writes[id] || { latest: null, running: false }
  writes[id].latest = job
  runWrite(id)
}

// Resolves once whatever is queued for this campaign has been tried.
const flush = (id) => new Promise((resolve) => {
  const w = writes[id]
  if (!w || (!w.running && !w.latest)) { resolve(); return }
  const prev = w.done
  w.done = () => { if (prev) prev(); resolve() }
})

/**
 * Pull this location's campaigns from the server into the cache. Anything
 * saved or deleted here that hasn't reached the server stays as it is here
 * and is sent again. The first run in a browser moves its campaigns up.
 */
// One pull at a time PER LOCATION: a pull started at another location (the
// switch happened mid-load) must not stand in for this one's.
let pulling = null
let pullingKey = null
export const hydrateCampaigns = () => {
  if (!online()) return Promise.resolve(listCampaigns())
  const key = KEY()
  if (pulling && pullingKey === key) return pulling
  pullingKey = key
  const mine = marketingBackend.campaignGroupList()
    .then((res) => {
      if (key !== KEY()) return listCampaigns() // switched location meanwhile
      const server = (res && res.campaigns) || []
      const serverIds = new Set(server.map(c => c.id))
      const local = listCampaigns()
      const deleted = readJson(DELETED(), {})
      const firstTime = !localStorage.getItem(MIGRATED())
      // Local ones to keep and send: unsent changes, and (the first time) every
      // campaign this browser had before the server stored them.
      const serverById = new Map(server.map(c => [c.id, c]))
      // An unsent change here only wins over the server's copy when it's the
      // newer one, and never turns a campaign another device approved (or
      // cancelled) back into a draft: that would lose the ids of what it
      // scheduled, and Cancel could no longer stop them.
      const staleAgainst = (c) => {
        const sv = serverById.get(c.id)
        if (!sv) return false
        if (String(sv.updatedAt || '') > String(c.updatedAt || '')) return true
        return sv.status !== 'draft' && (c.status || 'draft') === 'draft'
      }
      const pending = local
        .filter(c => !deleted[c.id] && (c._dirty || (firstTime && !serverIds.has(c.id))) && !staleAgainst(c))
        .map((c) => { const sv = serverById.get(c.id); return sv ? { ...c, created: { ...(sv.created || {}), ...(c.created || {}) } } : c })
      const pendingIds = new Set(pending.map(c => c.id))
      const merged = [
        ...pending.map(c => ({ ...c, _dirty: true })),
        ...server.filter(c => !pendingIds.has(c.id) && !deleted[c.id]),
      ].sort((a, b) => String(b.updatedAt || '').localeCompare(String(a.updatedAt || '')))
      write(merged)
      merged.filter(c => c._dirty).forEach(c => queue(c.id, { kind: 'save', campaign: c }))
      Object.keys(deleted).forEach(id => queue(id, { kind: 'delete' }))
      try { localStorage.setItem(MIGRATED(), '1') } catch (e) { /* ignore */ }
      return merged
    })
    .catch(() => listCampaigns())
    .finally(() => { if (pulling === mine) { pulling = null; pullingKey = null } })
  pulling = mine
  return mine
}

export const saveCampaign = (campaign, salonId) => {
  const now = new Date().toISOString()
  const id = campaign.id || `cmp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
  const saved = { status: 'draft', created: {}, ...campaign, id, createdAt: campaign.createdAt || now, updatedAt: now, _dirty: true }
  write([saved, ...listCampaigns(salonId).filter(c => c.id !== id)], salonId)
  // Only this location's campaigns go up from here (the server scopes by the
  // signed-in location); another location's copy is made there by the server.
  if (!salonId || String(salonId) === String(here())) queue(id, { kind: 'save', campaign: saved })
  return saved
}

export const removeCampaign = (id, salonId) => {
  const list = write(listCampaigns(salonId).filter(c => c.id !== id), salonId)
  if (!salonId || String(salonId) === String(here())) {
    writeJson(DELETED(), { ...readJson(DELETED(), {}), [id]: Date.now() })
    queue(id, { kind: 'delete' })
  }
  return list
}

export const __resetSync = () => { Object.keys(writes).forEach(k => delete writes[k]); pulling = null; pullingKey = null; refusedOnce = false }

export const subscribeCampaigns = (fn) => {
  const on = () => fn()
  window.addEventListener(EVENT, on)
  const onStorage = (e) => { if (e && String(e.key || '').startsWith('beu-campaigns-')) fn() }
  window.addEventListener('storage', onStorage)
  return () => { window.removeEventListener(EVENT, on); window.removeEventListener('storage', onStorage) }
}

// The campaign an email or text (by its server id) belongs to, if any
export const campaignOf = (apiId, list = listCampaigns()) => {
  if (!apiId) return null
  return list.find(c => c.status !== 'cancelled' && Object.values(c.created || {}).some(x => x && String(x.id) === String(apiId))) || null
}

// Where a campaign is up to: draft, scheduled (nothing gone yet), running
// (some gone), sent (all gone), or cancelled. `sent`/`total` count the items
// switched on.
//
// `server` is what the server says happened to its emails and texts
// (useServerStatuses); without it they're judged by the clock, as before. Any
// that didn't go (held for credits, called off, failed) make it 'attention'.
export const statusOf = (c, now = new Date(), server = null) => {
  const on = (c.items || []).filter(i => i.on)
  const states = c.status === 'approved' ? on.map(i => itemState(i, (c.created || {})[i.id], server, now)) : []
  // Something approving didn't make is counted by its time, as it always was
  const gone = states.filter((s, i) => s.id === 'sent' || s.id === 'due' || (s.id === 'unscheduled' && moment(on[i].at).isSameOrBefore(now))).length
  const problems = states.filter(s => s.problem).length
  const base = { sent: gone, total: on.length, problems }
  if (c.status === 'cancelled') return { ...base, id: 'cancelled', label: 'Cancelled' }
  if (c.status !== 'approved') return { ...base, id: 'draft', label: 'Draft' }
  if (problems) return { ...base, id: 'attention', label: problems === 1 ? '1 didn’t go' : `${problems} didn’t go` }
  if (states.some(s => s.id === 'checking')) return { ...base, id: 'checking', label: 'Checking…' }
  if (!gone) return { ...base, id: 'scheduled', label: 'Scheduled' }
  if (gone < on.length) return { ...base, id: 'running', label: `${gone} of ${on.length} sent` }
  return { ...base, id: 'sent', label: 'Sent' }
}

// The tab a campaign sits under on the Campaigns list
// (one where something didn't go sits with the sent ones, flagged)
export const tabOf = (c, now = new Date(), server = null) => {
  const s = statusOf(c, now, server).id
  return s === 'draft' ? 'draft' : s === 'sent' || s === 'cancelled' || s === 'attention' ? 'sent' : 'scheduled'
}

// A campaign to run again: new id, not approved, dates moved to the future
// keeping the same shape (next year for a season, next week otherwise).
export const duplicateCampaign = (c, now = new Date()) => {
  const first = moment(c.start || (c.items[0] && c.items[0].at))
  let shift = 0
  while (moment(first).add(shift, 'days').isSameOrBefore(now, 'day')) shift += c.planKey && c.planKey.startsWith('moment:') ? 364 : 7
  const move = (d) => (d ? moment(d).add(shift, 'days') : d)
  return {
    ...c,
    id: undefined, status: 'draft', created: {}, approvedAt: null, createdAt: undefined, planKey: null,
    name: `${c.name} (copy)`,
    start: c.start ? move(c.start).format('YYYY-MM-DD') : c.start,
    end: c.end ? move(c.end).format('YYYY-MM-DD') : c.end,
    // What was cancelled last time runs again this time
    items: c.items.map(({ cancelled, ...i }) => ({ ...i, on: cancelled ? true : i.on, at: move(i.at).toISOString(), post: i.post ? { ...i.post, id: undefined, status: 'draft' } : i.post })),
  }
}

// Copy to other locations: a draft at each, made by the server at that
// location (so its staff see it there), to approve with that location's
// clients, consent and credits. Free times came from this location's diary,
// so those posts are marked to check. Resolves to the copies; rejects with
// the server's reason.
export const copyToLocations = async (c, targets = []) => {
  const saved = listCampaigns().find(x => x.id === c.id) || saveCampaign(c)
  if (saved._dirty) queue(saved.id, { kind: 'save', campaign: saved })
  await flush(saved.id)
  const res = await marketingBackend.campaignGroupCopy(saved.id, targets.map(t => String(t.salonId)))
  return (res && res.copies) || []
}
